import React, { Component } from 'react';
import Api, { API_ROOT } from '@defi/core-api';
import './appBanner.css';

// Shared with the server-rendered MVC banner so a dismissal in either shell is honored in the other.
const DISMISS_PREFIX = 'appBanner-dismissed-';

// Top-anchored fixed/sticky chrome that must slide down by the banner height so it isn't painted
// under the banner. The DefiHeader AppBar is fixed at top:0; the DefiSidebar drawer paper is fixed
// at top:68 (hard-coded to the header height) — if only the header moves, the sidebar tucks under
// the lowered header. Both are offset by their ORIGINAL top + banner height.
const HEADER_SELECTOR = '[data-testid="defi-header"]';
const SIDEBAR_PAPER_SELECTOR = '[data-testid="defi-sidebar"] > div';
const OFFSET_SELECTORS = [HEADER_SELECTOR, SIDEBAR_PAPER_SELECTOR];

// Remembers each element's pre-offset top so repeated passes compose against the real baseline.
const ORIG_TOP_ATTR = 'data-app-banner-orig-top';

// Above the MUI AppBar (zIndex 1200) so the banner is never painted under the header,
// but below MUI modals/tooltips/snackbars (default zIndex ~1300+) so dialogs stay accessible.
const BANNER_Z_INDEX = 1250;

// Severity is presentation-only and arrives from the server. Allowlist the class to the known set so
// a bad/empty value can't inject an arbitrary class, and let appBanner.css own the colour per class
// (rather than trusting a raw server hex, which risked an unreadable strip e.g. white-on-white).
const ALLOWED_SEVERITY_CLASSES = ['app-banner--info', 'app-banner--warning', 'app-banner--critical'];
const DEFAULT_SEVERITY_CLASS = 'app-banner--info';
function resolveSeverityClass(severityCssClass) {
  return ALLOWED_SEVERITY_CLASSES.indexOf(severityCssClass) !== -1 ? severityCssClass : DEFAULT_SEVERITY_CLASS;
}

// An active banner with empty/non-string content would paint an empty fixed stripe that still
// reserves layout space — treat it as "no banner".
function hasMessage(messageHtml) {
  return typeof messageHtml === 'string' && messageHtml.trim().length > 0;
}

// Dismissal flags are SHARED with the server-rendered MVC banner (same key) so a dismissal in either
// shell is honored in the other. That sharing means this shell must NOT delete a flag the other shell
// may have just written: during a deploy the two briefly disagree on the active token, and blindly
// pruning every non-current key would wipe a fresh dismissal and re-show a dismissed banner. So we
// prune only flags this shell wrote (a plausible epoch-ms timestamp value) that are older than the
// retention window; the current key, and any flag we can't date (e.g. the MVC '1' value), are kept.
const DISMISSAL_RETENTION_MS = 90 * 24 * 60 * 60 * 1000; // 90 days
const MIN_PLAUSIBLE_TS = 1577836800000; // 2020-01-01; smaller values aren't our timestamps (e.g. '1')
function pruneDismissals(currentToken) {
  try {
    const currentKey = currentToken ? DISMISS_PREFIX + currentToken : null;
    const cutoff = Date.now() - DISMISSAL_RETENTION_MS;
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key || key.indexOf(DISMISS_PREFIX) !== 0 || key === currentKey) continue;
      const ts = Number(localStorage.getItem(key));
      if (Number.isFinite(ts) && ts >= MIN_PLAUSIBLE_TS && ts < cutoff) {
        localStorage.removeItem(key);
      }
    }
  } catch (e) {
    /* localStorage unavailable (private mode/quota) — non-fatal */
  }
}

/**
 * Global application banner for the React micro-UIs. Resolves the active banner for the current
 * client from the server (same resolver + sanitizer the MVC banner uses) and renders it pinned to
 * the top of the viewport. Rendered inside DefiLayout, so it appears on every authenticated shell
 * that uses the layout. A resolution failure renders nothing — the banner must never break the page.
 *
 * Layout ownership: this component owns its own positioning (fixed top:0, body padding-top to
 * reserve space, and pushing the fixed header down) rather than threading a prop through
 * DefiHeader/DefiLayout, because content clearance differs per consumer (padding class vs a
 * caller-supplied defaultClass/noPadding) and a generic margin tweak would not hold across all of
 * them. This mirrors the proven server-side approach and is layout-agnostic.
 */
class AppBanner extends Component {
  constructor(props) {
    super(props);
    this.setBannerRef = el => {
      this.bannerEl = el;
    };
    this.applyLayoutOffset = () => {
      const el = this.bannerEl;
      if (!el) return;
      const height = Math.ceil(el.getBoundingClientRect().height);
      if (!height) return; // not laid out yet — a later observer/fallback pass will catch it

      // Capture the host's pre-banner inline body padding-top once so we can restore it exactly on
      // dismiss/unmount instead of clobbering a value another component may have set.
      if (this.origBodyPaddingTop === null) {
        this.origBodyPaddingTop = document.body.style.paddingTop;
      }
      document.body.style.paddingTop = height + 'px';
      this.getOffsetTargets().forEach(t => {
        // Capture the original top once (header 0, sidebar 68) before moving it, so repeated passes
        // compose against the real baseline instead of accumulating.
        if (!t.hasAttribute(ORIG_TOP_ATTR)) {
          t.setAttribute(ORIG_TOP_ATTR, String(parseFloat(window.getComputedStyle(t).top) || 0));
        }
        const orig = parseFloat(t.getAttribute(ORIG_TOP_ATTR)) || 0;
        t.style.setProperty('top', orig + height + 'px', 'important');
      });
      this.offsetApplied = true;
    };
    this.handleDismiss = () => {
      const banner = this.state.banner;
      if (banner) {
        try {
          // Store the dismissal time (epoch ms) rather than a bare flag so pruneDismissals can age-out
          // this shell's old flags without ever deleting a fresh dismissal the MVC shell just wrote.
          localStorage.setItem(DISMISS_PREFIX + banner.versionToken, String(Date.now()));
        } catch (e) {
          /* non-fatal */
        }
      }
      this.teardownObserver();
      this.clearLayoutOffset();
      this.setState({
        banner: null
      });
    };
    this.state = {
      banner: null
    };
    this.bannerEl = null; // callback ref — works in React 15 (config-audit) and 16 alike
    this.offsetApplied = false;
    this.origBodyPaddingTop = null; // pre-banner inline body padding-top, captured to restore on clear
    this.resizeObserver = null;
    this.fallbackTimers = [];
  }
  componentDidMount() {
    this._mounted = true;
    this.fetchBanner();
  }
  componentDidUpdate() {
    // The banner just became visible — measure it and reserve layout space once.
    if (this.state.banner && this.bannerEl && !this.offsetApplied && !this.resizeObserver) {
      this.applyLayoutOffset();
      this.observeBanner();
    }
  }
  componentWillUnmount() {
    this._mounted = false;
    this.teardownObserver();
    this.clearLayoutOffset();
  }
  fetchBanner() {
    // Resolution must never break the page. Guard against a synchronous throw from Api.get (bad config
    // / unexpected runtime) as well as a rejected promise; both log via console.error so the failure
    // is visible to developers without surfacing anything to the user.
    try {
      Api.get(API_ROOT.root, 'AppBanner/Current').then(data => {
        if (!this._mounted) return;
        if (!data || !data.active || !hasMessage(data.messageHtml)) {
          pruneDismissals(null);
          return;
        }
        pruneDismissals(data.versionToken);
        // Edited banners re-appear because the version token changes, invalidating the old flag.
        if (data.dismissible && this.isDismissed(data.versionToken)) return;
        this.setState({
          banner: data
        });
      }).catch(err => {
        console.error('AppBanner resolution failed; banner suppressed', err);
      });
    } catch (err) {
      console.error('AppBanner resolution failed; banner suppressed', err);
    }
  }
  isDismissed(token) {
    try {
      return !!localStorage.getItem(DISMISS_PREFIX + token);
    } catch (e) {
      return false;
    }
  }

  // Top-anchored fixed/sticky chrome (header + sidebar paper) that must be pushed down. Normal-flow
  // elements scroll with the banner via the body padding and need no per-element offset.
  getOffsetTargets() {
    const targets = [];
    try {
      OFFSET_SELECTORS.forEach(sel => {
        const candidates = document.querySelectorAll(sel);
        for (let i = 0; i < candidates.length; i++) {
          const pos = window.getComputedStyle(candidates[i]).position;
          if (pos === 'fixed' || pos === 'sticky') targets.push(candidates[i]);
        }
      });
    } catch (e) {
      /* non-fatal */
    }
    return targets;
  }
  clearLayoutOffset() {
    if (!this.offsetApplied) return;
    document.body.style.paddingTop = this.origBodyPaddingTop || '';
    this.origBodyPaddingTop = null;
    this.getOffsetTargets().forEach(t => {
      // Restore the stylesheet-defined top by dropping our inline override.
      t.style.removeProperty('top');
      t.removeAttribute(ORIG_TOP_ATTR);
    });
    this.offsetApplied = false;
  }

  // Re-sync the offset when the banner gains height on reveal or changes height on text rewrap.
  observeBanner() {
    if (this.resizeObserver || !this.bannerEl) return;
    if (window.ResizeObserver) {
      try {
        this.resizeObserver = new window.ResizeObserver(this.applyLayoutOffset);
        this.resizeObserver.observe(this.bannerEl);
        return;
      } catch (e) {
        /* fall through to timed fallback */
      }
    }
    // Fallback for environments without ResizeObserver.
    this.fallbackTimers.push(window.setTimeout(this.applyLayoutOffset, 200));
    this.fallbackTimers.push(window.setTimeout(this.applyLayoutOffset, 800));
  }
  teardownObserver() {
    if (this.resizeObserver) {
      try {
        this.resizeObserver.disconnect();
      } catch (e) {
        /* non-fatal */
      }
      this.resizeObserver = null;
    }
    this.fallbackTimers.forEach(t => window.clearTimeout(t));
    this.fallbackTimers = [];
  }
  render() {
    const banner = this.state.banner;
    if (!banner || !hasMessage(banner.messageHtml)) return null;

    // Severity background is owned by the allowlisted CSS class (see appBanner.css), not the raw
    // server hex; #fff foreground is legible on all three severity backgrounds.
    const severityClass = resolveSeverityClass(banner.severityCssClass);
    return /*#__PURE__*/React.createElement("div", {
      id: "appBanner",
      ref: this.setBannerRef,
      className: "app-banner ".concat(severityClass),
      style: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: BANNER_Z_INDEX,
        color: '#fff'
      },
      "data-testid": "app-banner",
      "data-banner-version": banner.versionToken,
      "data-dismissible": String(!!banner.dismissible),
      role: "status",
      "aria-live": "polite",
      "aria-atomic": "true"
    }, /*#__PURE__*/React.createElement("div", {
      className: "app-banner__inner"
    }, /*#__PURE__*/React.createElement("span", {
      className: "app-banner__text",
      dangerouslySetInnerHTML: {
        __html: banner.messageHtml
      }
    })), banner.dismissible && /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "app-banner__dismiss",
      "aria-label": "Dismiss banner",
      "data-testid": "app-banner-dismiss",
      onClick: this.handleDismiss
    }, /*#__PURE__*/React.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      width: "14",
      height: "14",
      viewBox: "0 0 14 14",
      fill: "none",
      "aria-hidden": "true",
      focusable: "false"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M1.4 14L0 12.6L5.6 7L0 1.4L1.4 0L7 5.6L12.6 0L14 1.4L8.4 7L14 12.6L12.6 14L7 8.4L1.4 14Z",
      fill: "white"
    }))));
  }
}
export default AppBanner;